import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, Input, OnDestroy } from '@angular/core';
import * as echarts from 'echarts';
import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ChartWrapperService, ChartWrapperInstance } from '../../shared-services/chart-wrapper.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { buildConfigurableTooltipFormatter, buildConfigurableAxisLabel } from '../../utils/chart.utils';
import { buildSecondaryXAxisOption, buildSecondaryYAxisOption } from '../../utils/axis.utils';

import { CapitalCasePipe } from '../../pipes/capital-case.pipe';

@Component({
  selector: 'app-bar-chart',
  imports: [CommonModule, CapitalCasePipe, CapitalCasePipe],
  templateUrl: './bar-chart.component.html',
  styleUrl: './bar-chart.component.scss',
})
export class BarChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;
  protected myChart!: echarts.ECharts;
  protected allCategories: string[] = [];

  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();
  private configService = inject(ConfigService);
  private globalFilterService = inject(GlobalFilterService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  private reflowSub?: Subscription;
  private themeSub?: Subscription;
  private lastChartData?: { category: string[]; value: number[] };

  ngAfterViewInit(): void {
    // Attach wrapper — handles filter subscription, click events, legend sync, etc.
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.myChart,
      getSeriesNames: () => [],
      getCategories: () => this.allCategories,
    });

    // Initial load (uses pre-existing filters if available)
    this.loadAndRenderChart(initialFilters ?? undefined);

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });

    // Re-render with fresh theme tokens when the user switches theme at runtime.
    // Dispose + re-init is required because ECharts' registered 'dvm' theme
    // updates do NOT propagate to already-initialized chart instances.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.lastChartData || !this.myChart) return;
      const chartDom = document.getElementById(this.data.widgetId);
      if (!chartDom) return;
      this.myChart.dispose();
      this.myChart = echarts.init(chartDom, 'dvm');
      this.renderChart(this.lastChartData);
    });
  }

  protected loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data.widgetId);
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom, 'dvm');
    }

    this.myChart.showLoading();

    this.configService.getConfig(this.data.apiUrl, this.data, filters)?.subscribe({
      next: (res: any) => {
        if (!res) return;
        const dataKey = this.data?.dataKey;
        const raw = dataKey ? res?.[dataKey] : (res?.data || res);
        const catKey = this.data?.categoryKey || 'category';
        if (Array.isArray(raw)) {
          this.wrapper.setSourceData(raw, catKey);
        }
        let chartData: any;
        if (Array.isArray(raw)) {
          const valKey = this.data?.valueKey || 'value';
          chartData = {
            category: raw.map((item: any) => item[catKey]),
            value: raw.map((item: any) => item[valKey]),
          };
        } else {
          chartData = raw;
        }
        this.renderChart(chartData);
        this.addResizeListener();
      },
      error: () => {
        this.myChart?.showLoading();
      },
    });
  }

  protected renderChart(chartData?: { category: string[]; value: number[] }): void {
    if (!this.data) return;

    if (chartData) this.lastChartData = chartData;

    // Resolve effective theme: central + dashboard + widget overrides
    const theme = this.themeService.resolve(this.data?.themeOverrides);

    this.allCategories = chartData?.category || [];

    const tooltipCfg = this.data?.chartConfig?.tooltip;
    const formatter = buildConfigurableTooltipFormatter(tooltipCfg);
    const showTooltip = tooltipCfg?.show !== false;
    const labelsAllowed = this.data?.chartConfig?.showLabels !== false;
    const showDataLabels = labelsAllowed && this.data?.chartConfig?.showDataLabels === true;

    const categories = chartData?.category || [];
    const enableDoubleXAxis = this.data?.enableDoubleXAxis === true;
    const enableDoubleYAxis = this.data?.enableDoubleYAxis === true;

    // Build primary x-axis
    const primaryXAxis: any = {
      type: 'category',
      data: categories,
      triggerEvent: true,
      axisLabel: { show: labelsAllowed },
    };
    const secondaryX = buildSecondaryXAxisOption(this.data, categories);
    if (secondaryX && !labelsAllowed) {
      secondaryX.axis.axisLabel = { ...(secondaryX.axis.axisLabel || {}), show: false };
    }
    const xAxisOption = secondaryX ? [primaryXAxis, secondaryX.axis] : primaryXAxis;

    // Build primary y-axis
    const yAxisLabel = buildConfigurableAxisLabel(this.data?.chartConfig?.yAxis);
    const primaryYAxis: any = {
      type: 'value',
      triggerEvent: true,
      axisLabel: {
        ...(yAxisLabel || {}),
        show: labelsAllowed && yAxisLabel?.show !== false,
      },
    };
    const secondaryY = buildSecondaryYAxisOption(this.data);
    if (secondaryY && !labelsAllowed) {
      secondaryY.axisLabel = { ...(secondaryY.axisLabel || {}), show: false };
    }
    const yAxisOption = secondaryY ? [primaryYAxis, secondaryY] : primaryYAxis;

    const option = {
      tooltip: {
        show: showTooltip,
        trigger: tooltipCfg?.trigger || 'axis',
        backgroundColor: theme.tooltip?.bg || '#fff',
        borderColor: theme.tooltip?.borderColor || '#ccc',
        textStyle: {
          color: theme.tooltip?.textColor || '#333',
          fontSize: theme.tooltip?.fontSize || 13,
        },
        ...(formatter ? { formatter } : {}),
      },
      grid: {
        left: '3%',
        right: enableDoubleYAxis ? '8%' : '4%',
        top: '5%',
        bottom: enableDoubleXAxis ? '12%' : '3%',
        containLabel: true,
      },
      xAxis: xAxisOption,
      yAxis: yAxisOption,
      series: [
        {
          type: 'bar',
          data: chartData?.value || [],
          itemStyle: { color: theme.chart?.palette?.[0] || '#5470C6' },
          label: {
            show: showDataLabels,
            position: 'top',
          },
        },
      ],
    };

    applyRoleVisibilityToOption(option, this.data);

    this.myChart.setOption(option, true);
    setTimeout(() => {
      this.myChart.hideLoading();
    }, 1000);

    // Let wrapper re-apply visual state and wire click/legend events
    this.wrapper.afterRender();
  }

  protected addResizeListener(): void {
    window.addEventListener('resize', this.handleResize);
  }

  protected handleResize = (): void => {
    this.myChart?.resize();
  };

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    window.removeEventListener('resize', this.handleResize);
    this.myChart?.dispose();
  }
}
